import React, { useState, useEffect } from 'react';
import { 
  Building, Shield, Settings2, Save, RefreshCw, Plus, X, Loader2, 
  CheckCircle2, AlertTriangle, FileText, BadgePercent, Globe, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';
import TaxTableManager from '../components/TaxTableManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TaxSetupPage = () => {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.company_id;
  
  const [activeTab, setActiveTab] = useState('employer');
  const [loading, setLoading] = useState(false);
  
  // Employer Setup State
  const [employerSetup, setEmployerSetup] = useState({
    name: '',
    ein: '',
    address: '',
    city: '',
    state: 'CA',
    zip: '',
    state_id: '',
    contact_name: '',
    phone: '',
    email: '',
    pay_frequency: 'bi-weekly'
  });
  
  // Tax Configuration State
  const [taxConfig, setTaxConfig] = useState({
    federal_withholding: true,
    state_withholding: true,
    social_security: true,
    medicare: true,
    ca_sdi: true,  // California State Disability Insurance
    ca_sui: true,  // California State Unemployment Insurance
    futa: true     // Federal Unemployment Tax
  });

  const tabs = [
    { id: 'employer', label: 'Employer Setup', icon: Building },
    { id: 'federal', label: 'Federal Taxes', icon: Shield },
    { id: 'state', label: 'State Taxes', icon: MapPin },
    { id: 'local', label: 'Local Taxes', icon: Globe },
    { id: 'tables', label: 'Tax Tables', icon: BadgePercent }
  ];

  useEffect(() => {
    fetchEmployerSetup();
  }, [companyId]);

  const fetchEmployerSetup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer-setup${companyId ? `?company_id=${companyId}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        if (data) setEmployerSetup(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to fetch employer setup:', err);
    }
  };

  const handleSaveEmployer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/employer-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...employerSetup, company_id: companyId })
      });
      if (res.ok) {
        toast.success('Employer setup saved successfully');
      } else {
        toast.error('Failed to save employer setup');
      }
    } catch (err) {
      toast.error('Error saving employer setup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tax Setup</h1>
          <p className="text-slate-500">Configure employer information and payroll tax rates</p>
        </div>
        <div className="flex items-center gap-4">
          <a href="/payroll" className="text-sm text-sky-600 hover:underline">Go to Payroll →</a>
          <span className="text-sm text-slate-500">Company:</span>
          <span className="font-medium text-sky-700">{currentCompany?.name || 'Blue Skies Farm'}</span>
        </div>
      </div>

      {/* Connection Note */}
      <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
        <p className="text-sm text-sky-800">
          <Shield className="w-4 h-4 inline mr-1" />
          Tax rates configured here are used when calculating employee payroll. 
          Employee filing status (W-4/DE-4) is set in <a href="/payroll" className="underline">Payroll → Employees</a>.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'bg-white shadow-sm text-[#14213d] hover:bg-white' : ''}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Employer Setup Tab */}
      {activeTab === 'employer' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-sky-600" />
              Employer Information
            </CardTitle>
            <CardDescription>
              Business details for payroll tax filings (W-2, 941, DE-9)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Business Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Legal Business Name</label>
                <Input
                  value={employerSetup.name}
                  onChange={e => setEmployerSetup({...employerSetup, name: e.target.value})}
                  placeholder="Blue Skies Farm LLC"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Federal EIN</label>
                <Input
                  value={employerSetup.ein}
                  onChange={e => setEmployerSetup({...employerSetup, ein: e.target.value})}
                  placeholder="12-3456789"
                />
                <p className="text-xs text-slate-500 mt-1">9 digits, format: XX-XXXXXXX</p>
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Street Address</label>
                <Input
                  value={employerSetup.address}
                  onChange={e => setEmployerSetup({...employerSetup, address: e.target.value})}
                  placeholder="123 Farm Road"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">City</label>
                  <Input
                    value={employerSetup.city}
                    onChange={e => setEmployerSetup({...employerSetup, city: e.target.value})}
                    placeholder="Sacramento"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">State</label>
                  <select
                    className="w-full h-10 border rounded-md px-3"
                    value={employerSetup.state}
                    onChange={e => setEmployerSetup({...employerSetup, state: e.target.value})}
                  >
                    <option value="CA">California</option>
                    <option value="TX">Texas</option>
                    <option value="FL">Florida</option>
                    <option value="NY">New York</option>
                    <option value="WA">Washington</option>
                    {/* Add more states as needed */}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">ZIP Code</label>
                  <Input
                    value={employerSetup.zip}
                    onChange={e => setEmployerSetup({...employerSetup, zip: e.target.value})}
                    placeholder="95814"
                  />
                </div>
              </div>
            </div>

            {/* State Tax IDs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">State Employer ID (CA EDD)</label>
                <Input
                  value={employerSetup.state_id}
                  onChange={e => setEmployerSetup({...employerSetup, state_id: e.target.value})}
                  placeholder="000-0000-0"
                />
                <p className="text-xs text-slate-500 mt-1">For CA: EDD Account Number</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Pay Frequency</label>
                <select
                  className="w-full h-10 border rounded-md px-3"
                  value={employerSetup.pay_frequency}
                  onChange={e => setEmployerSetup({...employerSetup, pay_frequency: e.target.value})}
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="semi-monthly">Semi-Monthly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Contact Name</label>
                <Input
                  value={employerSetup.contact_name}
                  onChange={e => setEmployerSetup({...employerSetup, contact_name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone</label>
                <Input
                  value={employerSetup.phone}
                  onChange={e => setEmployerSetup({...employerSetup, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input
                  value={employerSetup.email}
                  onChange={e => setEmployerSetup({...employerSetup, email: e.target.value})}
                  placeholder="payroll@company.com"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveEmployer} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Employer Setup
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Federal Taxes Tab */}
      {activeTab === 'federal' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-600" />
              Federal Payroll Taxes
            </CardTitle>
            <CardDescription>
              Configure federal income tax withholding and FICA taxes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Federal Income Tax */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h3 className="font-medium text-slate-900 mb-3">Federal Income Tax Withholding</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Method</p>
                  <p className="font-medium">IRS Publication 15-T (2024)</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Form Used</p>
                  <p className="font-medium">W-4 (Employee's Withholding)</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Federal withholding is calculated based on employee W-4 and IRS tax brackets
              </p>
            </div>

            {/* FICA Taxes */}
            <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
              <h3 className="font-medium text-sky-900 mb-3">FICA Taxes (Social Security & Medicare)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Social Security (OASDI)</p>
                  <p className="text-xl font-bold text-slate-900">6.2%</p>
                  <p className="text-xs text-slate-500">Employee + 6.2% Employer</p>
                  <p className="text-xs text-amber-600">Wage Base: $168,600 (2024)</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Medicare (HI)</p>
                  <p className="text-xl font-bold text-slate-900">1.45%</p>
                  <p className="text-xs text-slate-500">Employee + 1.45% Employer</p>
                  <p className="text-xs text-amber-600">Additional 0.9% over $200K</p>
                </div>
              </div>
            </div>

            {/* FUTA */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="font-medium text-amber-900 mb-3">FUTA (Federal Unemployment Tax)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Gross Rate</p>
                  <p className="text-xl font-bold text-slate-900">6.0%</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">After SUTA Credit</p>
                  <p className="text-xl font-bold text-emerald-600">0.6%</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Wage Base</p>
                  <p className="text-xl font-bold text-slate-900">$7,000</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Employer-only tax. 5.4% credit for timely SUTA payments.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* State Taxes Tab */}
      {activeTab === 'state' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-600" />
              State Payroll Taxes - California
            </CardTitle>
            <CardDescription>
              California state income tax and employment taxes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* CA State Income Tax */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h3 className="font-medium text-slate-900 mb-3">California State Income Tax (PIT)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Tax Brackets</p>
                  <p className="font-medium">1% - 13.3%</p>
                  <p className="text-xs text-slate-500">Progressive based on income</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Form Used</p>
                  <p className="font-medium">DE-4 (Employee's Withholding)</p>
                </div>
              </div>
            </div>

            {/* CA SDI */}
            <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
              <h3 className="font-medium text-sky-900 mb-3">SDI (State Disability Insurance)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Employee Rate</p>
                  <p className="text-xl font-bold text-slate-900">1.1%</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Wage Base (2024)</p>
                  <p className="text-xl font-bold text-slate-900">$153,164</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">Max Annual</p>
                  <p className="text-xl font-bold text-slate-900">$1,684.80</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Employee-only tax. Reported in W-2 Box 14 as "CASDI"
              </p>
            </div>

            {/* CA SUI/ETT */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="font-medium text-amber-900 mb-3">SUI & ETT (Employer Taxes)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">SUI (Unemployment Insurance)</p>
                  <p className="text-xl font-bold text-slate-900">1.5% - 6.2%</p>
                  <p className="text-xs text-slate-500">New employer: 3.4%</p>
                  <p className="text-xs text-amber-600">Wage Base: $7,000</p>
                </div>
                <div className="p-3 bg-white rounded border">
                  <p className="text-sm text-slate-500">ETT (Employment Training Tax)</p>
                  <p className="text-xl font-bold text-slate-900">0.1%</p>
                  <p className="text-xs text-slate-500">Employer-only</p>
                  <p className="text-xs text-amber-600">Wage Base: $7,000</p>
                </div>
              </div>
            </div>

            {/* Tax Rate Summary */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h3 className="font-medium text-emerald-900 mb-3">California Payroll Tax Summary</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Tax</th>
                    <th className="text-right p-2">Employee</th>
                    <th className="text-right p-2">Employer</th>
                    <th className="text-right p-2">Wage Base</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2">State Income Tax (PIT)</td>
                    <td className="p-2 text-right">1% - 13.3%</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">No limit</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">SDI</td>
                    <td className="p-2 text-right">1.1%</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">$153,164</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">SUI</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">1.5% - 6.2%</td>
                    <td className="p-2 text-right">$7,000</td>
                  </tr>
                  <tr>
                    <td className="p-2">ETT</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">0.1%</td>
                    <td className="p-2 text-right">$7,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Local Taxes Tab */}
      {activeTab === 'local' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-sky-600" />
              Local Payroll Taxes
            </CardTitle>
            <CardDescription>
              City and county payroll taxes (if applicable)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-600">Local payroll taxes are not set up yet</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-xl mx-auto">
                Adding and editing city or county payroll taxes is not available in this version, and no local tax is
                taken from paychecks. Federal, state, Social Security, Medicare and SDI withholding are calculated as usual.
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Local taxes will be added together with Special Deductions (garnishments, 401(k) and similar).
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tax Tables Tab */}
      {activeTab === 'tables' && (
        <TaxTableManager companyId={companyId} />
      )}
    </div>
  );
};

export default TaxSetupPage;
